// @ts-strict-ignore
import { updatablePublishedAttributeFields } from 'flow/editor/components/NodeEditorDrawer/AttributeConfiguration/types';
import { getTagActionsByTagName, isActionOfType } from 'flow/service/actions';
import { isListEphemeral } from 'flow/service/lists';
import { isCustomListEphemeral } from 'flow/service/actions/services/customLists';
import type { CustomList } from 'flow/service/actions/services/customLists';
import { createEphemeralOutcome, isOutcomeEphemeral } from 'flow/service/outcomes';
import {
  createEphemeralPublishedAttribute,
  getPATokenFromAttributeAction,
  isPublishedAttributeEphemeral,
  getActionsByPublishedAttributeToken,
} from 'flow/service/publishedAttributes';
import { isReasonCodeGroupEphemeral } from 'flow/service/reasonCodes';
import { indexSnippetsMetadataByToken } from 'flow/service/snippets';
import type { SnippetMetadata } from 'flow/service/snippets';
import { createEphemeralTag, isTagEphemeral } from 'flow/service/tags';
import { computeNextName } from 'flow/service/util';
import { ActionType } from 'flow/store/engine/constants';
import type {
  TagObjectInAction, Tag, PublishedAttribute, Action, ReasonCodeGroup, List, Attribute,
} from 'flow/store/engine/types';
import { snapshotAndCloneWorkflowData, compileWorkflowDataDependencies, removeEphemeralOutcomeFromTags } from '../helpers';
import type { WorkflowState } from '../state';


export function updateWorkflowProps(
  state: WorkflowState,
  action: { payload: Partial<WorkflowState['workflowProps']> },
) {
  const nextProps = action.payload;
  const {
    outcomes,
    publishedAttributes,
    reasonCodeGroups,
    tags,
    tagTypes,
    workflow,
  } = nextProps;

  /**
   * todo BUILD-817: this reducer should handle the computation of the Actions that
   * need to be updated based on incoming props. this is not supported yet, but will
   * be added once all account-level elements are supported and their needs are
   * more clear
   *
   * Redux wraps the local state in a Proxy to support immutable state updates with
   * mutable notation. The `current` function grabs the value that's within the Proxy.
   * https://redux-toolkit.js.org/api/other-exports#current
   */
  // const nextActions = computeActionsToUpdateWithNewProps(
  //   current(state.workflowData.actions),
  //   current(state.workflowProps),
  //   nextProps,
  // );
  // state.workflowData.actions = nextActions;
  // Merge existing ephemeral PAs back into the incoming set to prevent
  // them from being dropped when the server-fetched props replace the store.
  let mergedPublishedAttributes = publishedAttributes;
  if (publishedAttributes !== undefined) {
    const existingEphemeralPAs = (state.workflowProps?.publishedAttributes ?? [])
      .filter(pa => pa.isEphemeral);
    if (existingEphemeralPAs.length > 0) {
      const incomingTokens = new Set(publishedAttributes.map(pa => pa.attributeToken));
      const ephemeralPAsToPreserve = existingEphemeralPAs
        .filter(pa => !incomingTokens.has(pa.attributeToken));
      mergedPublishedAttributes = [...publishedAttributes, ...ephemeralPAsToPreserve];
    }
  }

  // we explicitly check against undefined to allow setting a value to `null`
  state.workflowProps = {
    ...state.workflowProps,
    ...(outcomes !== undefined && { outcomes }),
    ...(mergedPublishedAttributes !== undefined && {
      publishedAttributes: mergedPublishedAttributes,
    }),
    ...(reasonCodeGroups !== undefined && { reasonCodeGroups }),
    ...(tags !== undefined && { tags }),
    ...(tagTypes !== undefined && { tagTypes }),
    ...(workflow !== undefined && { workflow }),
  };
}


export function addEphemeralTagToProps(
  state: WorkflowState,
  action: {
    payload: Parameters<typeof createEphemeralTag>[0];
  },
) {
  const { name, ...restOpts } = action.payload;

  const existingTagNames = state.workflowProps?.tags?.map(existingTag => existingTag.tag_name);
  const uniqueTagNames = new Set<string>(existingTagNames);

  const ephemeralTag = createEphemeralTag({
    name: computeNextName(name, uniqueTagNames),
    ...restOpts,
  });

  state.workflowProps?.tags?.push(ephemeralTag);
}


export function updateTagInProps(
  state: WorkflowState,
  action: { payload: { updatedTagData: TagObjectInAction; actionId: string } },
) {
  const { payload: { updatedTagData, actionId } } = action;
  // find the current tag from UI (cannot rely on name of editedTag as that may have changed)
  const focusedAction = state.workflowData.actions[actionId];
  const matchingOutcomes = (updatedTagData.outcomes ?? [])
    .map(selectedOutcome => state.workflowProps.outcomes.find(
      outcome => outcome.outcome === selectedOutcome.outcome,
    ))
    .filter(Boolean);
  const matchingTagProp = state.workflowProps.tags.find(
    tag => tag.tag_name === focusedAction.tag.name,
  );
  let updatedTag: Tag;
  if (!isTagEphemeral(matchingTagProp)) {
    // using explicit matching here to avoid accidentally updating name or overwriting uneditable values
    updatedTag = {
      ...matchingTagProp,
      suppression_interval: updatedTagData.suppressionInterval,
      associated_types: updatedTagData.associatedTypes,
      is_case_creating: updatedTagData.isCaseCreating,
      is_adverse_action_reason: updatedTagData.isAdverseActionReason,
      category_name: updatedTagData.categoryName,
      category_id: updatedTagData.categoryId,
      archived: updatedTagData.archived,
      description: updatedTagData.description,
      tag_color: updatedTagData.color,
      is_dirty: true,
      outcomes: matchingOutcomes,
    };
  } else {
    // update tag in props - combine editedTag (converted to wf prop) w/matching tag from props
    updatedTag = {
      ...createEphemeralTag({ name: updatedTagData.name, ...updatedTagData }),
      app_version_count: matchingTagProp.app_version_count,
      customer_tag_id: matchingTagProp.customer_tag_id,
      outcomes: matchingOutcomes,
    };
  }
  // snapshot/clone wf data before making any changes
  const {
    workflowData, workflowSnapshots,
  } = snapshotAndCloneWorkflowData(state);

  const actionsToUpdate = getTagActionsByTagName(workflowData.actions, focusedAction.tag.name);
  // loop over all actions + update w/new tag data
  for (const actionToUpdate of actionsToUpdate) {
    actionToUpdate.tag = {
      ...updatedTagData,
      ...(isTagEphemeral(matchingTagProp)
        ? { isEphemeral: true }
        : { isDirty: true }),
    };
  }
  const updatedTagProps = [
    ...state.workflowProps.tags
      .filter(tag => tag.tag_name !== matchingTagProp.tag_name),
    updatedTag,
  ];

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowProps = { ...state.workflowProps, tags: updatedTagProps };
  state.workflowSnapshots = workflowSnapshots;
}


export function addEphemeralOutcomeToProps(
  state: WorkflowState,
  action: {
    payload: Parameters<typeof createEphemeralOutcome>[0];
  },
) {
  const { name, ...restOpts } = action.payload;

  const existingOutcomeNames = (
    state.workflowProps?.outcomes?.map(existingOutcome => existingOutcome.outcome)
  );
  const uniqueOutcomeNames = new Set<string>(existingOutcomeNames);

  const ephemeralOutcome = createEphemeralOutcome({
    name: computeNextName(name, uniqueOutcomeNames),
    ...restOpts,
  });

  state.workflowProps?.outcomes?.push(ephemeralOutcome);
}


export function addEphemeralPublishedAttributeToProps(
  state: WorkflowState,
  action: { payload: { attributeData: Partial<PublishedAttribute> } },
) {
  const { attributeData } = action.payload;
  const ephemeralPA = createEphemeralPublishedAttribute(attributeData);
  state.workflowProps.publishedAttributes.push(ephemeralPA);
}


export function updatePublishedAttributeInProps(
  state: WorkflowState,
  action: {
    payload: {
      updatedPublishedAttributeData: Partial<PublishedAttribute>;
      updatedActionData: Action;
      actionId: string;
    };
  },
) {
  const { payload: { updatedPublishedAttributeData, updatedActionData, actionId } } = action;
  const focusedAction = state.workflowData.actions[actionId];
  const token = getPATokenFromAttributeAction(focusedAction);
  const matchingPublishedAttributeProp = state.workflowProps.publishedAttributes.find(
    pa => pa.attributeToken === token,
  );

  // Defensive filtering - only apply fields that are defined in updatablePublishedAttributeFields
  const editableUpdates = Object.fromEntries(
    Object.entries(updatedPublishedAttributeData).filter(
      ([key]) => updatablePublishedAttributeFields.includes(
        key as (typeof updatablePublishedAttributeFields)[number],
      ),
    ),
  ) as Partial<PublishedAttribute>;

  const updatedPublishedAttribute: PublishedAttribute = {
    ...matchingPublishedAttributeProp,
    ...editableUpdates,
    ...(isPublishedAttributeEphemeral(matchingPublishedAttributeProp)
      ? { isEphemeral: true }
      : { isDirty: true }),
  };
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  // PAs can be in WF up to 2x (as an input + as an output) - get all related actions
  const connectedActions = getActionsByPublishedAttributeToken(workflowData.actions, token);
  for (const actionToUpdate of connectedActions) {
    if (isActionOfType(actionToUpdate, ActionType.INPUT)) {
      actionToUpdate.input = {
        ...actionToUpdate.input,
        name: updatedPublishedAttribute.attributeName,
        publishedDetails: updatedPublishedAttribute,
        // update action-specific properties **only** if this is targeted action
        ...(actionToUpdate.publicId === updatedActionData.publicId && {
          jqFunction: updatedActionData.input.jqFunction,
          useJq: String(updatedActionData.input.useJq),
        }),
      };
    } else {
      actionToUpdate.output = {
        ...actionToUpdate.output,
        name: updatedPublishedAttribute.attributeName,
        publishedDetails: updatedPublishedAttribute,
        // update action-specific properties **only** if this is targeted action
        ...(actionToUpdate.publicId === updatedActionData.publicId && {
          defineValueRanges: updatedActionData.output.defineValueRanges,
          outputValueRangeOptions: updatedActionData.output.outputValueRangeOptions,
        }),
      };
      // update action-specific properties **only** if this is targeted action
      if (actionToUpdate.publicId === updatedActionData.publicId) {
        // CRA-specific action options - these include updated defaults for backward-compatibility
        actionToUpdate.options = updatedActionData.options;
      }
    }
  }
  const updatedPublishedAttributeProps = [
    ...state.workflowProps.publishedAttributes.filter(
      pa => pa.attributeToken !== matchingPublishedAttributeProp.attributeToken,
    ),
    updatedPublishedAttribute,
  ];

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowProps = {
    ...state.workflowProps,
    publishedAttributes: updatedPublishedAttributeProps,
  };
  state.workflowSnapshots = workflowSnapshots;
}


/**
 * Adds an ephemeral ReasonCodeGroup to props.
 * This reducer, as opposed to the other addXEphemeralProps reducers, does not
 * call a factory function for the reasonCodeGroup, as there is no UI flow to create
 * one where only partial data is available - it will always be created using an existing
 * ReasonCodeGroup object
 */
export function addEphemeralReasonCodeGroupToProps(
  state: WorkflowState,
  action: { payload: { reasonCodeGroup: ReasonCodeGroup } },
) {
  const { reasonCodeGroup } = action.payload;

  if (reasonCodeGroup.isEphemeral) {
    state.workflowProps.reasonCodeGroups.push(reasonCodeGroup);
  }
}


/**
 * Accepts an ephemeral list and adds to props
 */
export function addEphemeralListToProps(
  state: WorkflowState,
  action: { payload: { list: List } },
) {
  const { list } = action.payload;

  if (isListEphemeral(list)) {
    state.workflowProps.lists.push(list);
  }
}

export function addEphemeralCustomListToProps(
  state: WorkflowState,
  action: { payload: { customList: CustomList } },
) {
  const { customList } = action.payload;

  if (isCustomListEphemeral(customList)) {
    state.workflowProps?.customLists?.push(customList);
  }
}


/**
 * Adds an ephemeral attribute to a service in props.
 * Attributes belong to services, so we add the attribute to the specific service's attributes.
 */
export function addEphemeralAttributeToProps(
  state: WorkflowState,
  action: { payload: { attribute: Attribute; serviceInternalName: string } },
) {
  const { attribute, serviceInternalName } = action.payload;

  if (!attribute.isEphemeral) {
    return;
  }

  const service = state.workflowProps.services[serviceInternalName];
  if (service && attribute.attribute_id) {
    service.attributes[attribute.attribute_id] = attribute;
  }
}


export function removeEphemeralReasonCodeGroupFromProps(
  state: WorkflowState,
  action: { payload: { reasonCodeGroup: ReasonCodeGroup } },
) {
  const { reasonCodeGroup: reasonCodeGroupToDelete } = action.payload;

  if (!isReasonCodeGroupEphemeral(reasonCodeGroupToDelete)) {
    return;
  }

  state.workflowProps.reasonCodeGroups = state.workflowProps.reasonCodeGroups
    .filter(reasonCodeGroup => reasonCodeGroup.id !== reasonCodeGroupToDelete.id);
}


/**
 * used when swapping tags/outcomes and the `updateAction` reducer is used
 * otherwise, `removeAction` reducer handles ephemeral prop cleanup
 */
export function removeEphemeralProp(
  state: WorkflowState,
  action: { payload: { name: string; type: ActionType } },
) {
  const { name: ephemeralName, type } = action.payload;
  switch (type) {
    case ActionType.TAG: {
      /**
             * when is it ok to remove an ephemeral tag from props?
             * - if there is only ONE action using the ephemeral tag
             * if this condition is not met, do not remove from props
             */
      const isTagInWorkflow = getTagActionsByTagName(
        state.workflowData.actions,
        ephemeralName,
      ).length > 0;

      if (!isTagInWorkflow) {
        state.workflowProps.tags = state.workflowProps.tags.filter(
          tag => !(isTagEphemeral(tag) && tag.tag_name === ephemeralName),
        );
      }
      break;
    }
    case ActionType.OUTCOME: {
      // when removing an outcome, ensure that it is removed from everywhere (incl any tags using this)
      const { updatedActions, updatedTags } = removeEphemeralOutcomeFromTags({
        actions: state.workflowData.actions,
        tags: state.workflowProps.tags,
        outcomeName: ephemeralName,
      });

      state.workflowData.actions = updatedActions;
      state.workflowProps.tags = updatedTags;
      state.workflowProps.outcomes = state.workflowProps.outcomes.filter(
        outcome => !(isOutcomeEphemeral(outcome) && outcome.outcome === ephemeralName));
      break;
    }
    default:
  }
}


/**
 * Completely replaces the snippets metadata state in the global store.
 */
export function updateSnippetsMetadataInProps(
  state: WorkflowState,
  action: { payload: { snippetsMetadata: SnippetMetadata[] } },
) {
  const { payload } = action;

  state.workflowProps.snippets = indexSnippetsMetadataByToken(payload.snippetsMetadata);
}

